import '../App.css';

import { useEffect, useRef, useState } from 'react';
import { hasItems, isError, isProcessing, shouldShowResults } from '../types/pageState';
import { buildSeoUrl, seoConfig } from '../utils/seoConfig';

import { useNavigate } from 'react-router-dom';
import FilterControls from '../components/FilterControls';
import FoodDetailModal from '../components/FoodDetailModal';
import Header from '../components/Header';
import ItemsCounter from '../components/ItemsCounter';
import MenuSurvey from '../components/MenuSurvey';
import MenuItemCard from '../components/MenuItemCard';
import NonLoggedComponent from '../components/NonLoggedComponent';
import ProcessingModal from '../components/ProcessingModal';
import ShareableLink from '../components/ShareableLink';
import UploadSection from '../components/UploadSection';
import { useAuth } from '../hooks/useAuth';
import { useCamera } from '../hooks/useCamera';
import { useFilters } from '../hooks/useFilters';
import { useMenuProcessing } from '../hooks/useMenuProcessing';
import { usePricing } from '../hooks/usePricing';
import { useSeo } from '../hooks/useSeo';
import {
  completePendingSignup,
  captureProductEvent,
  updateProductAnalyticsContext,
} from '../utils/productAnalytics';

const MIN_ITEMS_BEFORE_REVEAL = 4;
const REVEAL_FALLBACK_DELAY_MS = 20000;

function HomePage() {
  const navigate = useNavigate();

  const { isLoading, isAuthenticated } = useAuth();
  const { userPlan } = usePricing(isAuthenticated);
  const { showCamera, stream, videoRef, canvasRef, startCamera, captureImage, stopCamera } = useCamera();

  const {
    selectedImage,
    previewUrl,
    imageCompressing,
    pageState,
    responseSectionRef,
    setDefaultPreview,
    handleImageSelect,
    handleCameraCapture,
    handleClearImage,
    startProcessing,
  } = useMenuProcessing();

  const {
    sortBy,
    setSortBy,
    filters,
    setFilters,
    allergenFilters,
    toggleNutrientFilter,
    clearAllFilters,
    processMenuItems,
  } = useFilters();

  const [selectedFoodItem, setSelectedFoodItem] = useState(null);
  const [showFoodModal, setShowFoodModal] = useState(false);
  const [hasRevealDelayElapsed, setHasRevealDelayElapsed] = useState(false);

  useEffect(() => {
    updateProductAnalyticsContext({ isAuthenticated, userPlan });
    completePendingSignup({ isAuthenticated, userPlan });
  }, [isAuthenticated, userPlan]);

  const resultsTopRef = useRef(null);
  const processingStartedRef = useRef(false);
  const hasScrolledToResultsRef = useRef(false);
  const pageIsProcessing = isProcessing(pageState);
  const visibleItemCount = pageState?.items?.filter(item => !item?.loading).length || 0;
  const hasEnoughItemsForReveal = visibleItemCount >= MIN_ITEMS_BEFORE_REVEAL;
  const canRevealResults = hasEnoughItemsForReveal || hasRevealDelayElapsed;

  useEffect(() => {
    setDefaultPreview();
  }, [selectedImage, setDefaultPreview]);

  useEffect(() => {
    if (selectedImage && !processingStartedRef.current && pageState.status === 'ready') {
      processingStartedRef.current = true;
      const processor = startProcessing(selectedImage);
      processor.connect();
    }

    if (!selectedImage) {
      processingStartedRef.current = false;
      hasScrolledToResultsRef.current = false;
      setHasRevealDelayElapsed(false);
    }
  }, [selectedImage, pageState.status, startProcessing]);

  useEffect(() => {
    if (!pageIsProcessing) {
      setHasRevealDelayElapsed(false);
      return undefined;
    }

    setHasRevealDelayElapsed(false);
    const revealTimeout = window.setTimeout(() => {
      setHasRevealDelayElapsed(true);
    }, REVEAL_FALLBACK_DELAY_MS);

    return () => window.clearTimeout(revealTimeout);
  }, [pageIsProcessing, pageState.sessionId]);

  useEffect(() => {
    if (!canRevealResults) {
      hasScrolledToResultsRef.current = false;
      return;
    }

    if (hasScrolledToResultsRef.current) return;

    const animationFrame = window.requestAnimationFrame(() => {
      if (!resultsTopRef.current || hasScrolledToResultsRef.current) return;

      hasScrolledToResultsRef.current = true;
      resultsTopRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [canRevealResults]);

  const handleUpgradeClick = () => {
    captureProductEvent('paywall-viewed', {
      trigger: 'upgrade_click',
      userPlan,
      sessionId: pageState?.sessionId,
    });
    navigate('/pricing');
  };

  const handleClearImageWithRef = () => {
    processingStartedRef.current = false;
    hasScrolledToResultsRef.current = false;
    clearAllFilters();
    setSortBy('menu_order');
    handleClearImage();
  };

  const openFoodModal = (item, index) => {
    captureProductEvent('menu-item-opened', {
      itemIndex: index,
      itemNameLength: item?.menuResponse?.length,
      hasImage: Boolean(item?.imageUrls?.length),
      sessionId: pageState?.sessionId,
    });
    captureProductEvent('menu-item-expanded', {
      itemIndex: index,
      hasImage: Boolean(item?.imageUrls?.length),
      sessionId: pageState?.sessionId,
    });
    const hasAllergenData = Object.keys(item || {}).some(key =>
      ['milk', 'eggs', 'peanuts', 'nuts', 'soybeans', 'fish', 'crustaceans', 'molluscs', 'celery', 'mustard'].includes(key)
    );
    if (hasAllergenData) {
      captureProductEvent('allergen-viewed', { itemIndex: index, sessionId: pageState?.sessionId });
    }
    setSelectedFoodItem({ ...item, originalIndex: index });
    setShowFoodModal(true);
  };

  const closeFoodModal = () => {
    setShowFoodModal(false);
    setSelectedFoodItem(null);
  };

  const handleCaptureImage = () => {
    captureImage(handleCameraCapture);
  };

  // File input is now handled by UploadSection component - no need for duplicate
  const title = 'Menu Image Generator | Turn Menus into Dish Photos | Menu AI';
  const description =
    '📸 Upload any restaurant menu and get dish images, nutrition, and allergen hints in seconds. Free to try—no photoshoot needed.';
  const canonical = buildSeoUrl('/');
  const imageAlt = 'Menu AI menu translation preview';
  const primaryPages = [
    { name: 'Menu scanner', url: buildSeoUrl('/menu-scanner') },
    { name: 'Menu to images', url: buildSeoUrl('/menu-to-image') },
    { name: 'Menu OCR', url: buildSeoUrl('/menu-ocr') },
    { name: 'For restaurants', url: buildSeoUrl('/for-restaurants') },
    { name: 'Pricing', url: buildSeoUrl('/pricing') },
    { name: 'Blog', url: buildSeoUrl('/blog') },
  ];

  useSeo({
    title,
    description,
    canonical,
    robots: 'index, follow',
    keywords:
      'menu image generator, menu scanner, restaurant menu to image, ai menu generator, dish image generator, menu ocr, menu digitization',
    og: {
      title,
      description,
      url: canonical,
      image: seoConfig.defaultImage,
      imageAlt,
      type: 'website',
      siteName: seoConfig.siteName,
      locale: seoConfig.locale,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      url: canonical,
      image: seoConfig.defaultImage,
      imageAlt,
      site: seoConfig.twitterHandle,
      creator: seoConfig.twitterHandle,
      domain: seoConfig.twitterDomain,
    },
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${seoConfig.baseUrl}/#organization`,
          name: seoConfig.siteName,
          alternateName: ['Menu Images', seoConfig.twitterDomain],
          url: seoConfig.baseUrl,
          logo: `${seoConfig.baseUrl}/favicon.png`,
          sameAs: ['https://x.com/jordibosch20'],
        },
        {
          '@type': 'SoftwareApplication',
          '@id': `${seoConfig.baseUrl}/#app`,
          name: seoConfig.siteName,
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          url: seoConfig.baseUrl,
          description,
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          publisher: { '@id': `${seoConfig.baseUrl}/#organization` },
        },
        {
          '@type': 'WebSite',
          '@id': `${seoConfig.baseUrl}/#website`,
          name: seoConfig.siteName,
          alternateName: ['Menu Images', seoConfig.twitterDomain],
          url: seoConfig.baseUrl,
          description,
          publisher: { '@id': `${seoConfig.baseUrl}/#organization` },
          hasPart: primaryPages.map(page => ({
            '@type': page.name === 'Blog' ? 'Blog' : 'WebPage',
            name: page.name,
            url: page.url,
          })),
        },
      ],
    },
  });

  return (
    <div className="App">
      <Header isLoading={isLoading} isAuthenticated={isAuthenticated} />

      <main className="main-content">
        <div className="container">
          {/* <div className="hero-section"> */}
          {/* <h3 className="hero-title"> 📸 Scan your menu ⛶ </h3> */}
          {/* <p className="hero-description">Get images, nutritional information and allergens risk</p> */}
          {/* </div> */}

          {!isAuthenticated && <NonLoggedComponent />}

          {isAuthenticated && (
            <>
              <UploadSection
                selectedImage={selectedImage}
                previewUrl={previewUrl}
                showCamera={showCamera}
                stream={stream}
                loading={pageIsProcessing}
                imageCompressing={imageCompressing}
                onImageSelect={handleImageSelect}
                onStartCamera={startCamera}
                onCaptureImage={handleCaptureImage}
                onStopCamera={stopCamera}
                onClearImage={handleClearImageWithRef}
                videoRef={videoRef}
                canvasRef={canvasRef}
              />
              {!selectedImage && <MenuSurvey placement="logged-in-home" />}
            </>
          )}

          {/* File input removed - now handled by UploadSection component */}

          {shouldShowResults(pageState) && (
            <div className="results-top-anchor" ref={resultsTopRef}>
              <FilterControls
                sortBy={sortBy}
                setSortBy={setSortBy}
                filters={filters}
                setFilters={setFilters}
                nutrientFilters={allergenFilters}
                toggleNutrientFilter={toggleNutrientFilter}
                clearAllFilters={clearAllFilters}
                userPlan={userPlan}
                onUpgradeClick={handleUpgradeClick}
              />
            </div>
          )}

          {hasItems(pageState) && (
            <div className="items-count-section">
              <ItemsCounter pageState={pageState} processMenuItems={processMenuItems} />
              <ShareableLink sessionId={pageState?.sessionId} />
            </div>
          )}

          <ProcessingModal isVisible={pageIsProcessing && !canRevealResults} />

          <div className="response-section" ref={responseSectionRef}>
            {isError(pageState) && (
              <div className="error">
                <span className="error-icon">❌</span>
                <div className="error-content">
                  <div className="error-title">{pageState.error || 'Failed to process menu'}</div>
                  {pageState.message && <div className="error-message">{pageState.message}</div>}
                </div>
              </div>
            )}
            {hasItems(pageState) && (
              <div className="success">
                <div className="menu-items-section">
                  <div className="menu-items-grid">
                    {(() => {
                      const items = pageState?.items;
                      const showPlaceholder = pageIsProcessing;
                      return processMenuItems(items, showPlaceholder, userPlan).map((item, index) => (
                        <MenuItemCard
                          key={`${item.loading ? 'loading' : 'item'}-${index}`}
                          item={item}
                          index={index}
                          onItemClick={openFoodModal}
                          onUpgradeClick={handleUpgradeClick}
                          userPlan={userPlan}
                        />
                      ));
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <FoodDetailModal
        item={selectedFoodItem}
        isVisible={showFoodModal}
        onClose={closeFoodModal}
        onUpgradeClick={handleUpgradeClick}
        userPlan={userPlan}
      />

      <footer className="footer">
        <nav className="footer-links" aria-label="Footer navigation">
          <a href="/menu-scanner">Menu scanner</a>
          <a href="/menu-to-image">Menu to images</a>
          <a href="/menu-ocr">Menu OCR</a>
          <a href="/for-restaurants">For restaurants</a>
          <a href="/pricing">Pricing</a>
          <a href="/blog">Blog</a>
        </nav>
        <p>
          Made with ❤️ by{' '}
          <a href="https://x.com/jordibosch20" target="_blank" rel="noopener noreferrer">
            @jordibosch20
          </a>
        </p>
      </footer>
    </div>
  );
}

export default HomePage;
